import { TABLE_LAYOUT_TOKENS } from "@shared/styles/tableLayoutTokens";

export const MANAGE_CONTACTS_LABELS = {
  PAGE_TITLE: "Manage Contacts",
  ALERTS_SECTION: "ALERTS",
  SHOW_ALERTS: "Show Alerts",
  CONTACT_PERMISSIONS: "CONTACT PERMISSIONS",
  CONTACT_PERMISSIONS_DESCRIPTION:
    "You'll see contacts for fund commitments where you're listed as a Primary or Secondary Contact. Please allow up to 2 days for updates to be reflected.",
  ADD_NEW_CONTACT: "Add New Contact",
  EXPORT_CURRENT_VIEW: "Export Current View",
  EDIT: "EDIT",
  ADD_CONTACT_ASSIGNMENT: "Add Contact Assignment",
  CANCEL: "Cancel",
  DISCARD_CHANGES: "Discard Changes",
  SAVE_CHANGES: "Save Changes",
  ABOUT_PERMISSIONS: "About Permissions",
  ACCOUNTS: "ACCOUNTS",
  FUNDS: "FUNDS",
  ROLES: "ROLES",
  DOCUMENT_ACCESS: "DOCUMENT ACCESS",
  NOTIFICATIONS: "NOTIFICATIONS",
  STATUS: "STATUS",
  ACTION: "ACTION",
  ALL_ACCOUNTS: "All Accounts",
  ALL_FUNDS: "All Funds",
  ALL_ROLES: "All Roles",
  ALL_DOCUMENT_ACCESS: "All Document Access",
  ALL_NOTIFICATIONS: "All Notifications",
  ALL_STATUSES: "All Statuses",
  CONTACT: "Contact",
  ACCOUNT: "Account",
  FUND: "Fund",
  ROLE: "Role",
  STATUS_COLUMN: "Status",
  LAST_LOGIN: "Last Login",
  CAPITAL_CALLS: "Capital Calls",
  LEGAL_DOCUMENTS: "Legal Documents",
  MORE: "More",
  LESS: "Less",
  ROWS_PER_PAGE: TABLE_LAYOUT_TOKENS.PAGE_SIZE,
  CLEAR_FILTERS: "Clear Filters",
  NO_ROLES_SET: "No Roles Set",
  NO_DOCUMENT_ACCESS_SET: "No Document Access Set",
  NO_NOTIFICATIONS_SET: "No Notifications Set",
  NONE_SET: "None Set",
  REMOVE_ROW_ARIA: "Remove contact assignment",
  UNDO_ROW_ARIA: "Undo unsaved changes for this row",
} as const;

export const ADD_CONTACT_FIELD_GRID = {
  THIRD: { xs: 12, sm: 6, md: 4, lg: 4 },
  TWO_THIRDS: { xs: 12, sm: 12, md: 8, lg: 8 },
  HALF: { xs: 12, sm: 6, md: 6, lg: 6 },
} as const;

export const ADD_CONTACT_LABELS = {
  DIALOG_TITLE: "ADD NEW CONTACT",
  INTRO_LINE_1:
    "Add a new contact by entering their details below. You'll assign Account and Fund access or copy permissions in the next step.",
  INTRO_LINE_2: "New contacts are reviewed before being added to fund commitments.",
  SECTION_CONTACT_INFO: "Contact Information",
  STEP_FUND_IFC: "Fund / IFC Access",
  STEP2_INTRO_ASSIGN: "Assign fund access and roles for {name}.",
  STEP2_INTRO_RULE:
    "You can only assign permissions for fund commitments where you are a Primary or Secondary Contact. Each selected account must include at least one fund and role.",
  STEP2_INTRO_CLONE:
    "Cloning a contact automatically applies the selected contact's account assignments, roles, document access, and notifications. This will replace any selections you have already made.",
  STEP2_SELECT_EXISTING_USER: "SELECT EXISTING USER TO CLONE PERMISSIONS FROM (OPTIONAL)",
  STEP2_CLONE_DEFAULT: "No User Selected",
  STEP2_SELECT_ACCOUNTS: "SELECT ACCOUNTS*",
  STEP2_SELECT_FUNDS: "SELECT FUNDS*",
  STEP2_ROLES: "ROLES*",
  STEP2_DOCUMENT_ACCESS: "DOCUMENT ACCESS",
  STEP2_NOTIFICATIONS: "NOTIFICATIONS",
  STEP2_REMOVE_ACCOUNT: "Remove Account",
  STEP2_REMOVE_FUND: "Remove Fund",
  STEP2_ROLES_EMPTY: "No Roles Assigned - At least One Must Be Selected",
  STEP2_NOTIFICATIONS_EMPTY: "None Selected",
  CANCEL: "Cancel",
  GO_BACK: "Go Back",
  ASSIGN_PERMISSIONS: "Assign Permissions",
  ADD_CONTACT: "Add Contact",
  CLOSE: "Close",
  ADD_ANOTHER: "Add Another Contact",
  SUCCESS_PREFIX: "The new contact,",
  SUCCESS_SUFFIX: "has been submitted and will be reviewed.",
} as const;

export const ADD_CONTACT_VALIDATION_COLOR = "#AB8433";

export const ADD_CONTACT_VALIDATION_MESSAGES = {
  firstNameRequired: "Please enter a first name",
  lastNameRequired: "Please enter a last name",
} as const;

// Edit-mode dropdown dimensions and styling tokens (per LPC-10819 visual spec)
export const EDIT_DROPDOWN_DIMS = {
  FIELD_W: 150,
  FIELD_H: 50,
  MENU_W: 250,
  FIRST_TEXT_PX: 14,
  MORE_TEXT_PX: 12,
  TEXT_GAP_PX: 5,
} as const;

export const ACTION_COL_PX = 100;

// Unsaved-change visual tokens
export const DIRTY_CELL_BG = "#EBF3FB";
export const DIRTY_OPTION_BG = "#D8E8F7";

export const CONTACT_TABLE_COLUMNS = [
  {
    key: "name",
    label: MANAGE_CONTACTS_LABELS.CONTACT,
    width: TABLE_LAYOUT_TOKENS.COLUMN_WIDTHS.contact,
    sortable: true,
    fixed: true,
  },
  {
    key: "account",
    label: MANAGE_CONTACTS_LABELS.ACCOUNT,
    width: TABLE_LAYOUT_TOKENS.COLUMN_WIDTHS.account.min,
    sortable: true,
    minWidth: true,
  },
  {
    key: "fund",
    label: MANAGE_CONTACTS_LABELS.FUND,
    width: TABLE_LAYOUT_TOKENS.COLUMN_WIDTHS.fund.min,
    sortable: true,
    minWidth: true,
  },
  {
    key: "role",
    label: MANAGE_CONTACTS_LABELS.ROLE,
    width: TABLE_LAYOUT_TOKENS.COLUMN_WIDTHS.role,
    sortable: true,
    fixed: true,
  },
  {
    key: "documentAccess",
    label: MANAGE_CONTACTS_LABELS.DOCUMENT_ACCESS,
    width: TABLE_LAYOUT_TOKENS.COLUMN_WIDTHS.documentAccess,
    sortable: true,
    fixed: true,
  },
  {
    key: "notifications",
    label: MANAGE_CONTACTS_LABELS.NOTIFICATIONS,
    width: TABLE_LAYOUT_TOKENS.COLUMN_WIDTHS.notifications,
    sortable: true,
    fixed: true,
  },
  {
    key: "status",
    label: MANAGE_CONTACTS_LABELS.STATUS_COLUMN,
    width: TABLE_LAYOUT_TOKENS.COLUMN_WIDTHS.status,
    sortable: true,
    fixed: true,
  },
  {
    key: "lastLogin",
    label: MANAGE_CONTACTS_LABELS.LAST_LOGIN,
    width: TABLE_LAYOUT_TOKENS.COLUMN_WIDTHS.lastLogin,
    sortable: true,
    fixed: true,
  },
] as const;

// Action column shown only in edit mode (appended to the right of CONTACT_TABLE_COLUMNS)
export const ACTION_COLUMN = {
  key: "action",
  label: MANAGE_CONTACTS_LABELS.ACTION,
  width: ACTION_COL_PX,
  sortable: false,
  fixed: true,
} as const;

// Editable cell column keys (drive the dropdown rendering in edit mode)
export type EditableColumnKey = "role" | "documentAccess" | "notifications";
export const EDITABLE_COLUMN_KEYS: EditableColumnKey[] = ["role", "documentAccess", "notifications"];

export const ALPINVEST_ROLE = "Carlyle Alpinvest";

// Role display order (for sorting roles in the UI) - same as My Profile
export const ROLE_ORDER: string[] = [
  "Primary Contact",
  "Secondary Contact",
  "Tax Advisor",
  "Legal Advisor",
  "Finance Contact",
  "AML/KYC Contact",
  "Wire Approver",
  ALPINVEST_ROLE,
];

// LPC-10833 — In the Internal-only Manage Contacts view, the role cell is only
// editable on rows whose saved roles include one of these values. Per the BA spec:
// "ONLY IFCs that have the Primary Contact Role Assigned can edit their role".
// Secondary Contact and Tax Advisor (and any other non-Primary role) render as
// read-only role chips in edit mode. This restriction does NOT apply to the
// hybrid / external-only views, which keep today's behaviour where any role cell
// is editable.
export const EDITABLE_ROLE_FOR_INTERNAL_ONLY: ReadonlySet<string> = new Set(["Primary Contact"]);

/**
 * Returns true when at least one of the supplied roles is editable under the
 * Internal-only role-edit gate (LPC-10833). The gate is checked against the
 * row's saved / baseline roles — not the in-progress draft — so a user cannot
 * lock themselves out of the dropdown mid-edit by removing the Primary Contact
 * role they were editing.
 */
export const isRoleEditableInInternalOnly = (roles: readonly string[] | undefined | null): boolean => {
  if (!roles || roles.length === 0) return false;

  return roles.some((role) => EDITABLE_ROLE_FOR_INTERNAL_ONLY.has(role));
};

// Document Access options
export const DOCUMENT_ACCESS_OPTIONS = [
  "Capital Call",
  "Distribution",
  "Financial Reporting",
  "Legal Information",
  "Tax Information",
] as const;

// Notification options
export const NOTIFICATION_OPTIONS = [
  "Capital Call",
  "Distribution",
  "Financial Reporting",
  "Legal Information",
  "Tax Information",
] as const;

// Status options - includes all statuses from Manage Contacts
export const STATUS_OPTIONS = [
  "Creation Pending",
  "In Review",
  "Action Required",
  "Removal Pending",
  "Up to Date",
] as const;

// Role color mapping - hex codes for each role type (same as My Profile)
export const ROLE_COLORS: Record<string, string> = {
  "Primary Contact": "#1D6571",
  "Secondary Contact": "#2198AC",
  "Wire Approver": "#5F5F5F",
  "AML/KYC Contact": "#EBEBEB",
  "Finance Contact": "#C5F9BA",
  "Legal Advisor": "#F2E8D3",
  "Tax Advisor": "#B6E7FA",
} as const;

// Mapping from API document access values to display values
export const DOCUMENT_ACCESS_API_TO_DISPLAY_MAP: Record<string, string> = {
  "Capital Call Management": "Capital Call",
  "Distribution Management": "Distribution",
  // Add other mappings if needed in the future
} as const;

/**
 * Maps API document access value to display value
 * @param apiValue - The value from the API (e.g., "Capital Call Management")
 * @returns The display value (e.g., "Capital Call") or the original value if no mapping exists
 */
export const mapDocumentAccessToDisplay = (apiValue: string): string => {
  return DOCUMENT_ACCESS_API_TO_DISPLAY_MAP[apiValue] || apiValue;
};

// Mapping from API notification values to display values
export const NOTIFICATION_API_TO_DISPLAY_MAP: Record<string, string> = {
  "Capital Call Management Notification": "Capital Call",
  "Distribution Management Notification": "Distribution",
  "Financial Reporting Notification": "Financial Reporting",
  "Legal Information Notification": "Legal Information",
  "Tax Information Notification": "Tax Information",
  // Add other mappings if needed in the future
} as const;

/**
 * Maps API notification value to display value
 * @param apiValue - The value from the API (e.g., "Capital Call Management Notification")
 * @returns The display value (e.g., "Capital Call") or the original value if no mapping exists
 */
export const mapNotificationToDisplay = (apiValue: string): string => {
  return NOTIFICATION_API_TO_DISPLAY_MAP[apiValue] || apiValue;
};
